import { useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../_theme/palette.js';
import { hexWithAlpha } from '../_theme/palette.js';

interface TextFieldProps {
  palette: Palette;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'number' | 'date' | 'datetime-local' | 'time' | 'month' | 'tel' | 'url';
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  // Numeric constraints for `type="number"`. Browsers enforce these via
  // the native increment buttons and constrainable validity API; the
  // validation engine catches them again at submit time as a backstop.
  min?: number;
  max?: number;
  step?: number;
  ariaLabel?: string;
  // Optional leading icon (e.g. envelope for email, phone for tel) — matches
  // the main app's icon-prefixed inputs from InputPreviews.
  leadingIcon?: JSX.Element;
  // Optional trailing icon (e.g. calendar for date) — sits on the right edge.
  trailingIcon?: JSX.Element;
}

export function TextField({
  palette,
  value,
  onChange,
  type = 'text',
  placeholder,
  minLength,
  maxLength,
  min,
  max,
  step,
  ariaLabel,
  leadingIcon,
  trailingIcon,
}: TextFieldProps): JSX.Element {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const input = (
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      placeholder={placeholder}
      minLength={minLength}
      maxLength={maxLength}
      min={type === 'number' ? min : undefined}
      max={type === 'number' ? max : undefined}
      step={type === 'number' ? step : undefined}
      aria-label={ariaLabel}
      style={{
        ...inputBaseStyle(palette, focused, hovered),
        paddingLeft: leadingIcon ? 40 : 14,
        paddingRight: trailingIcon ? 40 : 14,
      }}
    />
  );
  if (!leadingIcon && !trailingIcon) return input;
  return (
    <div style={{ position: 'relative' }}>
      {leadingIcon && (
        <span aria-hidden="true" style={iconWrapperStyle('left', palette)}>
          {leadingIcon}
        </span>
      )}
      {input}
      {trailingIcon && (
        <span aria-hidden="true" style={iconWrapperStyle('right', palette)}>
          {trailingIcon}
        </span>
      )}
    </div>
  );
}

function iconWrapperStyle(side: 'left' | 'right', palette: Palette): CSSProperties {
  return {
    position: 'absolute',
    [side]: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: side === 'right' ? palette.primary : hexWithAlpha(palette.textMuted, 0.7),
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  };
}

interface TextAreaProps {
  palette: Palette;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
}

export function TextArea({ palette, value, onChange, placeholder, rows = 4, maxLength }: TextAreaProps): JSX.Element {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  return (
    <textarea
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      style={{ ...inputBaseStyle(palette, focused, hovered), minHeight: 96, resize: 'vertical' }}
    />
  );
}

// Centralised input style. Three states: idle, hover (subtle border lift),
// focus (accent border + soft 2px ring). The easing curve is cubic-bezier
// instead of ease so the motion feels considered rather than bouncy.
const PREMIUM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export function inputBaseStyle(
  palette: Palette,
  focused = false,
  hovered = false,
): CSSProperties {
  const borderColor = focused
    ? palette.primary
    : hovered
      ? hexWithAlpha(palette.text, 0.22)
      : palette.border;
  return {
    width: '100%',
    padding: '12px 14px',
    borderRadius: palette.radius,
    border: `1px solid ${borderColor}`,
    background: palette.card,
    color: palette.text,
    fontFamily: 'inherit',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    transition: `border-color 180ms ${PREMIUM_EASE}, box-shadow 180ms ${PREMIUM_EASE}`,
    boxShadow: focused ? `0 0 0 4px ${hexWithAlpha(palette.primary, 0.14)}` : 'none',
  };
}
